import { CurrencyPipe, DatePipe } from '@angular/common';
import {
  Action,
  EditableTransaction,
  Transaction,
  Bucket,
  UpdateTransactionPayload,
  DeleteTransactionsAction,
  RestoreTransactionsAction,
  CancelTransactionsAction,
  EditTransactionAction,
  EditManyTransactionsAction,
  EditManyTransactionsActionValue,
} from '../../types';
import { ChangeDetectionStrategy, Component, OnInit, input, output, inject } from '@angular/core';
import { TransactionService } from '../../services/transaction.service';
import { BucketService } from '../../services/bucket.service';
import { map, take } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { UiFieldComponent } from '../../ui/field/field.component';
import { UiCurrencyInputComponent } from '../../ui/currency-input/currency-input.component';
import { UiInputDirective, UiTextareaDirective } from '../../ui/input/input.directive';
import { UiDatePickerComponent } from '../../ui/date-picker/date-picker.component';
import { UiButtonDirective } from '../../ui/button/button.directive';
import { SplitTransactionComponent } from '../split-transaction/split-transaction.component';
import { UiRadioGroupComponent } from '../../ui/radio-group/radio-group.component';
import { UiRadioGroupOption } from '../../ui/radio-group/radio-group-option';
import { SelectBucketTypeaheadComponent } from '../../shared/select-bucket-typeahead/select-bucket-typeahead.component';
import { LabelTypeaheadComponent } from '../../labels/label-typeahead/label-typeahead.component';
import { UiIconComponent } from '../../ui/icon/icon.component';
import { EditTransactionRowComponent } from './edit-transaction-row/edit-transaction-row.component';
import { merchantInitial } from '../../transaction-rules/transaction-rule-display';
import {
  effectiveTransactionPlaidAccount,
  hasTransactionBankImportDetails,
  hasTransactionSyncedFinancialFacts,
  transactionInstitutionLogoSrc,
  transactionPlaidAccountDisplayName,
} from '../transaction-plaid-account-display';

interface EditManyTransactionsFormValue {
  merchant: string;
  bucketId: string;
  description: string;
  labelIds: string[];
}

@Component({
  selector: 'app-edit-transactions',
  templateUrl: './edit-transactions.component.html',
  styleUrl: './edit-transactions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    style: 'display: flex; flex: 1 1 0%; min-height: 0; flex-direction: column;',
  },
  imports: [
    CurrencyPipe,
    DatePipe,
    ReactiveFormsModule,
    FormsModule,
    UiFieldComponent,
    UiCurrencyInputComponent,
    UiInputDirective,
    UiDatePickerComponent,
    UiButtonDirective,
    SplitTransactionComponent,
    UiRadioGroupComponent,
    SelectBucketTypeaheadComponent,
    UiTextareaDirective,
    LabelTypeaheadComponent,
    AsyncPipe,
    UiIconComponent,
    EditTransactionRowComponent,
  ],
})
export class EditTransactionsComponent implements OnInit {
  private bucketService = inject(BucketService);
  private transactionService = inject(TransactionService);

  readonly transactions = input<Array<Transaction>>([]);
  readonly transactionUpdatedEvent = output<Action>();
  readonly transactionTypeOptions: UiRadioGroupOption<string>[] = [
    { label: 'Spent', value: 'EXPENSE' },
    { label: 'Received', value: 'INCOME' },
  ];

  // Seeded from the loaded transaction before the form renders.
  transactionModel!: EditableTransaction;
  selectedBucket?: Bucket;
  suggestedBucketPreselected = false;
  multi: EditManyTransactionsFormValue = {
    merchant: '',
    bucketId: '',
    description: '',
    labelIds: [],
  };
  loading = false;
  private splitCondenseChildId?: string;
  buckets$ = this.bucketService.nonArchivedBuckets$;
  bucket$ = this.buckets$.pipe(map(buckets => buckets.find(bucket => bucket.id === this.transactions()[0]?.bucketId)));

  ngOnInit() {
    if (!this.isMultiple) {
      const transaction = this.transactions()[0];
      if (!transaction) return;
      this.transactionModel = { ...transaction, children: transaction.children?.map(child => ({ ...child })) };
      this.buckets$.pipe(take(1)).subscribe(buckets => {
        const currentBucket = buckets.find(bucket => bucket.id === transaction.bucketId);
        if (currentBucket) {
          this.selectedBucket = currentBucket;
          return;
        }
        // LCL-72: uncategorized transactions pre-select their top suggested budget,
        // matching the list rows — Update then commits the suggestion in one click.
        const suggestedBucket = transaction.suggestedBucketIds
          ?.map(bucketId => buckets.find(bucket => bucket.id === bucketId))
          .find(bucket => !!bucket);
        if (suggestedBucket) {
          this.selectedBucket = suggestedBucket;
          this.suggestedBucketPreselected = true;
        }
      });
    }
  }

  onSelectedBucketChange(bucket: Bucket | undefined): void {
    this.selectedBucket = bucket;
    this.suggestedBucketPreselected = false;
  }

  /**
   * Update only enables when something would actually change: dirty form fields,
   * a different budget, or an active split — split construction flows through
   * comboboxes and model mutations that never mark the form dirty, and
   * splitInvalid() already guards whether the split itself is submittable.
   */
  hasChanges(formDirty: boolean): boolean {
    if (formDirty) return true;
    if (this.transactionModel?.showSplit && this.transactionModel?.children?.length) return true;
    if ((this.transactionModel?.children?.length ?? 0) !== (this.transactions()[0]?.children?.length ?? 0)) return true;
    const originalBucketId = this.transactions()[0]?.bucketId ?? null;
    return (this.selectedBucket?.id ?? null) !== originalBucketId;
  }

  get isMultiple(): boolean {
    return this.transactions().length > 1;
  }

  get firstTransaction(): Transaction | undefined {
    return this.transactions()[0];
  }

  bankSourceTransaction(): Transaction | undefined {
    const transaction = this.firstTransaction;
    if (!transaction) return undefined;
    return transaction.parent ?? transaction;
  }

  bankImportTransaction(): Transaction | undefined {
    const transaction = this.bankSourceTransaction();
    if (!transaction || !hasTransactionBankImportDetails(transaction)) return undefined;
    return transaction;
  }

  get syncedFinancialFactsLocked(): boolean {
    return hasTransactionSyncedFinancialFacts(this.firstTransaction);
  }

  get hasPlaidSyncedTransactionSelection(): boolean {
    return this.transactions().some(transaction => hasTransactionSyncedFinancialFacts(transaction));
  }

  bankPlaidAccount(transaction: Transaction) {
    return effectiveTransactionPlaidAccount(transaction);
  }

  bankAccountDisplayName = transactionPlaidAccountDisplayName;
  bankInstitutionLogoSrc = transactionInstitutionLogoSrc;

  // Same broken-logo handling as transaction-list-item-content: institutions without a
  // servable logo 404 and render the browser's broken-image glyph — hide the img instead.
  protected readonly merchantInitial = merchantInitial;
  private readonly failedLogoSrcs = new Set<string>();

  logoFailed(src: string): boolean {
    return this.failedLogoSrcs.has(src);
  }

  onLogoError(src: string): void {
    this.failedLogoSrcs.add(src);
  }

  bankStatementDescription(transaction: Transaction): string | null {
    return transaction.merchantMeta?.originalName ?? null;
  }

  merchantDisplayName(): string {
    return this.transactionModel?.merchantMeta?.displayName || this.transactionModel?.merchant || '';
  }

  merchantLogoUrl(): string | null {
    return this.transactionModel?.merchantMeta?.logoUrl || null;
  }

  isIncome(): boolean {
    const transaction = this.transactionModel;
    if (!transaction) return false;
    // Manual edits preview from the selected type. netValue stays at the saved sign until the
    // server rewrites it, so a positive leftover would keep an income hero after Spent is chosen.
    if (!this.syncedFinancialFactsLocked) return transaction.type === 'INCOME';
    return transaction.type === 'INCOME' || (transaction.netValue ?? 0) > 0;
  }

  submitUpdate(transactionModel: UpdateTransactionPayload) {
    const updatePayload = this.toUpdatePayload(transactionModel);
    // A cleared picker must clear the bucket — preserving the old id made un-categorizing a silent no-op.
    updatePayload.bucketId = this.selectedBucket?.id ?? null;
    this.loading = true;
    const action: EditTransactionAction = {
      type: 'EDIT_TRANSACTION',
      value: updatePayload,
    };

    return this.transactionUpdatedEvent.emit(action);
  }

  splitOutOfBalance(): boolean {
    if (!this.transactionModel?.children?.length) return false;
    return Math.abs(this.splitTotalAmount() - this.originalSplitAmount()) >= 0.005;
  }

  splitInvalid(): boolean {
    if (!this.transactionModel?.children?.length) return false;
    const hasPersistedChild = this.transactionModel.children.some(child => !!child.id);
    return (
      this.transactionModel.children.length < 1 ||
      (this.transactionModel.children.length < 2 && !hasPersistedChild) ||
      this.transactionModel.children.some(child => !child.bucketId) ||
      this.splitOutOfBalance()
    );
  }

  startSplit(): void {
    if (this.transactionModel.parent) {
      this.splitCondenseChildId = this.transactionModel.id;
      this.transactionModel = {
        ...this.transactionModel.parent,
        showSplit: true,
        children: (this.transactionModel.parent.children ?? []).map(child => ({ ...child })),
      };
      this.selectedBucket = undefined;
      return;
    }

    this.transactionModel.showSplit = true;
    this.splitCondenseChildId = undefined;
    if (this.transactionModel.children?.length) return;

    const {
      children: _children,
      showSplit: _showSplit,
      selectedBucket: _selectedBucket,
      id: _id,
      ...childDraft
    } = this.transactionModel;
    this.transactionModel.children = [
      {
        ...childDraft,
        // Split children use '' for unset (matching the empty second row below).
        bucketId: this.selectedBucket?.id ?? this.transactionModel.bucketId ?? '',
        amount: this.transactionModel.amount,
      },
      {
        ...childDraft,
        bucketId: '',
        amount: 0,
        labelIds: [],
      },
    ];
  }

  collapseSplit(): void {
    if (!this.transactionModel.children?.length) {
      this.transactionModel.showSplit = false;
      return;
    }

    const persistedChildren = this.transactionModel.children.filter(child => !!child.id);
    if (!persistedChildren.length) {
      delete this.transactionModel.children;
      this.transactionModel.showSplit = false;
      return;
    }

    const childToKeep = persistedChildren.find(child => child.id === this.splitCondenseChildId) ?? persistedChildren[0];

    if (!childToKeep) {
      this.transactionModel.showSplit = false;
      return;
    }

    this.transactionModel.children = [
      {
        ...childToKeep,
        amount: this.transactionModel.amount,
      },
    ];
    this.transactionModel.showSplit = false;
  }

  submitMultiUpdate(multi: EditManyTransactionsFormValue) {
    this.loading = true;

    const value: EditManyTransactionsActionValue = {
      ids: this.transactions().map(transaction => transaction.id),
    };
    if (multi.merchant && !this.hasPlaidSyncedTransactionSelection) value.merchant = multi.merchant;
    if (this.selectedBucket) value.bucketId = this.selectedBucket.id;
    if (multi.description) value.description = multi.description;
    if (multi.labelIds.length) value.labelIds = multi.labelIds;

    const action: EditManyTransactionsAction = { type: 'EDIT_MANY_TRANSACTIONS', value };
    return this.transactionUpdatedEvent.emit(action);
  }

  canDelete(transactions: Transaction[]) {
    return !transactions.some(transaction => !!(transaction.parent || transaction.deleted));
  }

  canRestore(transactions: Transaction[]) {
    return !transactions.some(transaction => !transaction.deleted);
  }

  deleteSingleTransaction(transaction: Transaction) {
    const action: DeleteTransactionsAction = { type: 'DELETE_TRANSACTIONS', value: [transaction] };
    this.transactionUpdatedEvent.emit(action);
  }

  deleteTransactions(transactions: Transaction[]) {
    const action: DeleteTransactionsAction = { type: 'DELETE_TRANSACTIONS', value: transactions };
    this.transactionUpdatedEvent.emit(action);
  }

  restoreTransactions(transactions: Transaction[]) {
    const action: RestoreTransactionsAction = { type: 'RESTORE_TRANSACTIONS', value: transactions };
    this.transactionUpdatedEvent.emit(action);
  }

  cancel() {
    const action: CancelTransactionsAction = { type: 'CANCEL' };
    this.transactionUpdatedEvent.emit(action);
  }

  private toUpdatePayload(transactionModel: UpdateTransactionPayload): UpdateTransactionPayload {
    const editableTransaction = transactionModel as EditableTransaction;
    const transactionWithoutUiState = { ...editableTransaction };
    delete transactionWithoutUiState.showSplit;

    if (!editableTransaction.children?.length) return transactionWithoutUiState;

    const children = editableTransaction.children.map(child => {
      const transactionChild = { ...child };
      delete transactionChild.parent;
      return transactionChild;
    });

    if (editableTransaction.parent) {
      if (children.length === 1) {
        const child = children[0];
        if (!child) return transactionWithoutUiState;
        return {
          ...editableTransaction.parent,
          ...child,
          id: editableTransaction.parent.id,
        };
      }

      return {
        ...editableTransaction.parent,
        children,
      };
    }

    return {
      ...transactionWithoutUiState,
      children,
    };
  }

  private originalSplitAmount(): number {
    return this.transactionModel.parent ? this.transactionModel.parent.amount : this.transactionModel.amount;
  }

  private splitTotalAmount(): number {
    return (this.transactionModel.children ?? []).reduce((acc, curr) => parseFloat((curr.amount + acc).toFixed(2)), 0);
  }
}
